import { useEffect, useState } from "react";
import type { Report } from "@brutally/shared";
import { scoreBand } from "@brutally/shared";
import { gradeFor, QUEST_POINTS } from "../data/game.js";
import { GameWordmark } from "../components/GameWordmark.js";
import { StatBar } from "../components/StatBar.js";
import { PipMeter } from "../components/PipMeter.js";
import { useCountUp } from "../lib/useCountUp.js";
import { useInView } from "../lib/useInView.js";

interface Props {
  data: Report;
  onRerun: () => void;
  onShare: () => void;
  onSave: () => void;
  toast?: string;
}

export function ReportGame({ data, onRerun, onShare, onSave, toast }: Props) {
  const [mounted, setMounted] = useState(false);
  const [stamp, setStamp] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setMounted(true), 200);
    const t2 = setTimeout(() => setStamp(true), 1500);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  const score = useCountUp(data.score, mounted, 1700);
  const band = scoreBand(data.score);
  const grade = gradeFor(data.score);

  const [statRef, statSeen] = useInView<HTMLDivElement>(0.3);
  const [gaugeRef, gaugeSeen] = useInView<HTMLDivElement>(0.3);

  const [done, setDone] = useState<Record<number, boolean>>({});
  const gained = data.sevenDays.reduce((s, _d, i) => s + (done[i] ? QUEST_POINTS[i] : 0), 0);
  const projected = Math.min(92, data.score + gained);
  const allPts = QUEST_POINTS.reduce((a, b) => a + b, 0);
  const projGrade = gradeFor(projected);
  const toggle = (i: number) => setDone((d) => ({ ...d, [i]: !d[i] }));

  return (
    <div className="greport screen-enter">
      <header className="greport-bar">
        <div className="wrap greport-bar-inner">
          <GameWordmark size={19} onClick={onRerun} />
          <div className="greport-bar-right">
            <span className="pill greport-url">{data.url}</span>
            <button className="gbtn greport-bar-btn" onClick={onShare}>Share score</button>
            <button className="gbtn gbtn--primary greport-bar-btn" onClick={onSave}>＋ Save &amp; track</button>
            <button className="gbtn gbtn--yellow greport-bar-btn" onClick={onRerun}>Play again</button>
          </div>
        </div>
      </header>

      <section className="gverdict">
        <div className="wrap gverdict-inner">
          <div className="gverdict-score gcard">
            <div className="gverdict-score-top">
              <span className="glabel">Launch readiness</span>
              <span className="pill pill--red">Final score</span>
            </div>
            <div className="gverdict-num-row">
              <span className="gdisplay gverdict-num">
                {score}
                <span className="gverdict-denom gmono">/100</span>
              </span>
              <span
                className={`grade-stamp gverdict-grade ${stamp ? "is-stamped" : ""}`}
                style={{ background: grade.bg, color: grade.fg, opacity: stamp ? 1 : 0 }}
              >
                {grade.letter}
              </span>
            </div>
            <h2 className="gdisplay gverdict-word">{band.word}</h2>
            <p className="soft gverdict-band-note">{band.note}</p>
          </div>

          <div className="gverdict-body">
            <span className="pill pill--yellow">The verdict</span>
            <p className="gverdict-text">{data.verdict}</p>
            {data.said && (
              <div className="gverdict-gap gcard">
                <div className="gvg-side">
                  <span className="glabel">You said</span>
                  <p className="gvg-quote">"{data.said}"</p>
                </div>
                <span className="vs-badge gpixel gvg-vs">VS</span>
                <div className="gvg-side gvg-side--bad">
                  <span className="glabel" style={{ color: "var(--red)" }}>Your site said</span>
                  <p className="gvg-quote">"{data.saw}"</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="gbosses">
        <div className="wrap">
          <div className="gsection-head">
            <h3 className="gdisplay gsection-title">3 bosses between you and launch.</h3>
            <span className="pill pill--red">Beat them in order</span>
          </div>
          <div className="gboss-list">
            {data.killers.map((k, i) => (
              <article key={k.n} className="gboss gcard pop" style={{ animationDelay: `${0.05 + i * 0.1}s` }}>
                <div className="gboss-head">
                  <span className="gboss-n gdisplay">{k.n}</span>
                  <div className="gboss-titles">
                    <span className="gpixel gboss-tag">BOSS {i + 1}</span>
                    <h4 className="gdisplay gboss-title">{k.title}</h4>
                  </div>
                </div>
                <div className="gboss-detail">
                  <div className="gboss-cost">
                    <span className="glabel" style={{ color: "var(--red)" }}>✕ Damage it does</span>
                    <p>{k.cost}</p>
                  </div>
                  <div className="gboss-fix">
                    <span className="glabel" style={{ color: "var(--green)" }}>➜ How to beat it</span>
                    <p>{k.fix}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="gwins">
        <div className="wrap">
          <div className="gsection-head">
            <h3 className="gdisplay gsection-title">Achievements unlocked.</h3>
            <span className="pill pill--green">Genuinely earned</span>
          </div>
          <div className="gwin-list">
            {data.wins.map((w, i) => (
              <div key={i} className="gwin gcard pop" style={{ animationDelay: `${i * 0.08}s` }}>
                <span className="gwin-badge">★</span>
                <h5 className="gdisplay gwin-title">{w.title}</h5>
                <p className="soft gwin-body">{w.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="gstats" ref={statRef}>
        <div className="wrap">
          <div className="gsection-head">
            <h3 className="gdisplay gsection-title">Your stats.</h3>
            <span className="pill">Lower is bleeding</span>
          </div>
          <div className="gstats-grid">
            {data.scorecard.map((row, i) => (
              <div key={i} className="gstat-line">
                <span className="gstat-lens">{row.lens}</span>
                <StatBar score={row.score} run={statSeen} />
              </div>
            ))}
          </div>

          <div className="gspecials" ref={gaugeRef}>
            <p className="glabel gspecials-label" style={{ color: "var(--blue)" }}>
              The two stats nobody else shows you
            </p>
            <div className="gspecials-row">
              {data.gauges.map((g) => {
                const color = g.score < 45 ? "var(--red)" : "var(--green)";
                return (
                  <div key={g.key} className="gspecial gcard">
                    <div className="gspecial-head">
                      <span className="gpixel gspecial-key" style={{ color }}>{g.key}</span>
                      <span className="glabel">{g.label}</span>
                    </div>
                    <PipMeter score={g.score} run={gaugeSeen} color={color} />
                    <p className="soft gspecial-note">{g.note}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="gquest">
        <div className="wrap">
          <div className="gsection-head">
            <h3 className="gdisplay gsection-title">Quest log: the next 7 days.</h3>
            <span className="pill pill--yellow">Tick fixes. Watch your score climb.</span>
          </div>

          <div className="gquest-layout">
            <ol className="gquest-list">
              {data.sevenDays.map((d, i) => {
                const easy = d.tag === "Quick win";
                return (
                  <li
                    key={i}
                    className={`gquest-item gcard ${done[i] ? "is-done" : ""}`}
                    onClick={() => toggle(i)}
                  >
                    <span className={`gquest-check ${done[i] ? "is-checked" : ""}`}>{done[i] ? "✓" : ""}</span>
                    <div className="gquest-main">
                      <span className="gpixel gquest-day">{d.day}</span>
                      <span className="gquest-task">{d.task}</span>
                    </div>
                    <div className="gquest-meta">
                      <span className={`pill ${easy ? "pill--green" : "pill--red"} gquest-diff`}>
                        {easy ? "Easy" : "Hard"}
                      </span>
                      <span className="gpixel gquest-pts">+{QUEST_POINTS[i]}</span>
                    </div>
                  </li>
                );
              })}
            </ol>

            <aside className="gquest-side">
              <div className="gquest-meter gcard">
                <span className="glabel">Projected launch score</span>
                <div className="gquest-proj-row">
                  <span className="gdisplay gquest-proj" style={{ color: projGrade.bg }}>{projected}</span>
                  <span className="grade-stamp gquest-proj-grade" style={{ background: projGrade.bg, color: projGrade.fg }}>
                    {projGrade.letter}
                  </span>
                </div>
                <div className="meter gquest-proj-meter">
                  <div className="meter-track">
                    <div
                      className="meter-fill"
                      style={{ width: `${projected}%`, background: projGrade.bg, transition: "width .4s var(--pop)" }}
                    />
                  </div>
                </div>
                <div className="gquest-proj-from">
                  <span className="gpixel">FROM {data.score}</span>
                  <span className="gpixel">+{gained} XP of {allPts}</span>
                </div>
                <p className="soft gquest-proj-note">
                  {gained === 0
                    ? "Tick a quest to see what each fix is worth."
                    : projected >= 80
                    ? "Now that's a launch. Go ship it."
                    : "Keep going. You're climbing out of the red."}
                </p>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="gone">
        <div className="wrap gone-inner">
          <span className="pill pill--red">The one sentence</span>
          <blockquote className="gdisplay gone-quote">{data.oneSentence}</blockquote>
          <div className="gone-actions">
            <button className="gbtn gbtn--primary gbtn--lg" onClick={onShare}>
              Share this score <span aria-hidden="true">&rarr;</span>
            </button>
            <button className="gbtn gbtn--lg" onClick={onRerun}>Play another site</button>
          </div>
        </div>
      </section>

      <footer className="greport-foot">
        <div className="wrap greport-foot-inner">
          <GameWordmark size={16} />
          <span className="glabel faint">Brutally honest since you pressed start.</span>
        </div>
      </footer>

      {toast && <div className="gtoast gpixel">{toast}</div>}
    </div>
  );
}
